import React, {useEffect, useMemo, useState} from "react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {
    AlertTriangle, CalendarDays, CalendarOff, CalendarX, Clock, CreditCard, Download, Fingerprint, PenLine, ScanFace, Users, X,
} from "lucide-react";
import {ListEmpty, ListLoader, Panel, thClass} from "../../stock/StockUI";
import {inputClass as formInputClass} from "../../ClientsTable/clientFormUI";
import {restHoursLabel, restKindLabel} from "../../calander/restDays";
import Button from "../Common/Button";
import DateInput from "../Common/DateInput";
import {
    SOURCE_LABELS,
    VERIFY_MODE_LABELS,
    apiErrorMessage,
    fetchAttendanceSummary,
    formatDayLabel,
    formatDuration,
    presetRange,
    toDateKey,
} from "./attendanceApi";
import {buildPersonRow, buildRows, eachDate, firstPunchDate, timelineWindow} from "./attendanceStatus";
import {DayTimeline, EmployeeCalendar, KpiStrip, Legend, PresenceGrid, StatusPill} from "./PresenceViews";
import {useRestCalendar, useRestEditor} from "./useRestCalendar";

const inputClass = `${formInputClass(false).replace("w-full ", "")} disabled:opacity-60`;
const cardClass = "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg";
const tdClass = "px-6 py-4 text-sm";

const MODE_ICONS = {fingerprint: Fingerprint, face: ScanFace, card: CreditCard, manual: PenLine};

const Spinner = () => <ListLoader label="Chargement des pointages..."/>;

const CalendarWarning = () => (
    <div role="alert" className="flex items-center gap-2 px-4 sm:px-6 py-3 text-sm bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-b border-amber-200 dark:border-amber-800">
        <AlertTriangle className="w-4 h-4 flex-shrink-0"/>
        Repos et congés du calendrier indisponibles : les absences ne sont pas calculées.
    </div>
);

const formatLongDate = (key) =>
    new Date(`${key}T12:00:00`).toLocaleDateString("fr-FR", {weekday: "long", day: "2-digit", month: "long", year: "numeric"});

const PunchChip = ({punch}) => {
    const Icon = MODE_ICONS[punch.verify_mode] || Clock;
    const color = punch.duplicate
        ? "bg-gray-100 text-gray-400 line-through dark:bg-gray-700"
        : punch.kind === "in"
            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
            : "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
    const title = [
        punch.duplicate ? "Doublon ignoré" : punch.kind === "in" ? "Entrée" : "Sortie",
        VERIFY_MODE_LABELS[punch.verify_mode],
        SOURCE_LABELS[punch.source],
        punch.note,
    ].filter(Boolean).join(" · ");
    return (
        <span title={title} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${color}`}>
            <Icon className="w-3 h-3"/> {punch.time}
        </span>
    );
};

// ─── Détail d'un employé ─────────────────────────────────────────────────────

// That date's month, up to today at most (same as "Ce mois" on the current month)
const monthOf = (dateKey) => {
    const date = new Date(`${dateKey}T12:00:00`);
    const end = toDateKey(new Date(date.getFullYear(), date.getMonth() + 1, 0));
    const today = toDateKey(new Date());
    return {
        from: toDateKey(new Date(date.getFullYear(), date.getMonth(), 1)),
        to: end < today || dateKey > today ? end : today,
    };
};

const averageTime = (times) => {
    if (!times.length) return null;
    const avg = Math.round(times.reduce((sum, t) => {
        const [h, m] = t.split(":").map(Number);
        return sum + h * 60 + m;
    }, 0) / times.length);
    return `${String(Math.floor(avg / 60)).padStart(2, "0")}:${String(avg % 60).padStart(2, "0")}`;
};

const DayPunches = ({day}) => (
    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {day.punches.map((p) => {
            const Icon = MODE_ICONS[p.verify_mode] || Clock;
            return (
                <li key={p.id} className={`flex items-center gap-3 py-1.5 text-sm ${p.duplicate ? "opacity-50" : ""}`}>
                    <span className="w-12 font-semibold text-gray-900 dark:text-gray-100">{p.time}</span>
                    <span className={`w-28 font-medium ${p.duplicate ? "text-gray-400 line-through" : p.kind === "in" ? "text-green-700 dark:text-green-400" : "text-orange-700 dark:text-orange-400"}`}>
                        {p.duplicate ? "Doublon ignoré" : p.kind === "in" ? "Entrée" : "Sortie"}
                    </span>
                    <span className="inline-flex items-center gap-1 text-gray-500"><Icon className="w-4 h-4"/> {VERIFY_MODE_LABELS[p.verify_mode] || "–"}</span>
                    <span className="ml-auto text-xs text-gray-400 truncate">{[SOURCE_LABELS[p.source], p.note].filter(Boolean).join(" · ")}</span>
                </li>
            );
        })}
    </ul>
);

// What happened on one day, and what can be done about it
const DayDetail = ({cell, person, editor}) => {
    const canDeclare = editor.canManage && !person.unknown && ["absent", "pending", "none"].includes(cell.status);
    const offRest = ["rest", "leave"].includes(cell.status) ? cell.rests[0] : null;
    return (
        <div className={`${cardClass} p-4`}>
            <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="flex-1 min-w-[12rem] text-sm font-semibold text-gray-800 dark:text-gray-100 capitalize">{formatLongDate(cell.date)}</h3>
                <StatusPill status={cell.status}/>
                {cell.day && <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatDuration(cell.day.worked_minutes)}</span>}
            </div>
            {cell.workedOnRest && (
                <p className="text-xs text-violet-700 dark:text-violet-300 mb-2">A travaillé pendant son repos ou son congé.</p>
            )}
            {cell.rests.map((rest) => (
                <p key={rest.id} className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {restKindLabel(rest)} : {restHoursLabel(rest)}, du {formatDayLabel(rest.start_date)} au {formatDayLabel(rest.end_date)}
                </p>
            ))}
            {cell.day && <DayPunches day={cell.day}/>}
            {canDeclare && (
                <Button variant="tinted" size="sm" icon={CalendarOff} className="mt-1" onClick={() => editor.openLeave(person.key, cell.date)}>
                    Déclarer un congé ou un repos
                </Button>
            )}
            {offRest && editor.canManage && (
                <Button variant="tinted" size="sm" icon={PenLine} className="mt-1" onClick={() => editor.openEdit(offRest)}>
                    Modifier ce {restKindLabel(offRest).toLowerCase()}
                </Button>
            )}
        </div>
    );
};

const EmployeeAttendanceModal = ({person, listPeriod, initialView = "calendar", initialDate, editor, onClose}) => {
    // A single day is too little to read a history: start on its month then.
    const initial = listPeriod.from === listPeriod.to ? monthOf(listPeriod.from) : {from: listPeriod.from, to: listPeriod.to};
    const [range, setRange] = useState(initial);
    const [view, setView] = useState(person.unknown ? "punches" : initialView);
    const [selectedDate, setSelectedDate] = useState(initialDate || null);
    const fixedPresets = [
        {key: "week", label: "Cette semaine", range: presetRange("week")},
        {key: "month", label: "Ce mois", range: presetRange("month")},
        {key: "lastMonth", label: "Mois dernier", range: presetRange("lastMonth")},
    ];
    // The list's period gets its own button unless it's already one of those
    const presets = fixedPresets.some((p) => p.range.from === initial.from && p.range.to === initial.to)
        ? fixedPresets
        : [{key: "list", label: "Période de la liste", range: initial}, ...fixedPresets];

    const {data, isLoading, isError} = useQuery({
        queryKey: ["attendance", "summary", {...range, employeeId: ""}],
        queryFn: () => fetchAttendanceSummary({...range, employeeId: ""}),
        placeholderData: keepPreviousData,
    });
    const calendar = useRestCalendar(range.from);
    const today = toDateKey(new Date());
    const days = useMemo(() => (data?.days || []).filter((d) => d.employee_key === person.key), [data, person.key]);
    const sortedDays = [...days].sort((a, b) => b.date.localeCompare(a.date));

    const row = useMemo(() => buildPersonRow({
        person,
        days,
        dates: eachDate(range.from, range.to),
        restDays: calendar.restDays,
        hours: calendar.hours,
        today,
        since: firstPunchDate(data?.days || []),
        calendarReady: calendar.ready && !!data,
    }), [person, days, range, calendar.restDays, calendar.hours, calendar.ready, today, data]);

    const absences = row.cells.filter((c) => c.status === "absent").reverse();
    const selectedCell = row.cells.find((c) => c.date === selectedDate);
    const closed = days.filter((d) => !d.open);
    const stats = [
        {label: "Jours", value: days.length},
        {label: "Total", value: formatDuration(row.minutes)},
        {label: "Moy. / jour", value: closed.length ? formatDuration(Math.round(closed.reduce((s, d) => s + d.worked_minutes, 0) / closed.length)) : "–"},
        {label: "Arrivée moy.", value: averageTime(days.map((d) => d.first_in).filter(Boolean)) || "–"},
        {label: "Sorties manq.", value: row.missingOut, alert: row.missingOut > 0},
        ...(person.unknown ? [] : [
            {label: "Absences", value: calendar.ready ? row.absences : "–", alert: row.absences > 0},
            {label: "Repos", value: row.rests},
            {label: "Congés", value: row.leaves},
        ]),
    ];

    const tabs = [
        {key: "calendar", label: "Calendrier"},
        {key: "punches", label: `Pointages (${days.length} j)`},
        {key: "absences", label: `Absences (${data && calendar.ready ? absences.length : "…"})`},
    ];

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-40" onClick={onClose}>
            <div className="h-full w-full max-w-2xl bg-white dark:bg-gray-900 shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-4 sm:px-6 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 className={`text-lg font-semibold ${person.unknown ? "text-amber-600" : "text-gray-900 dark:text-gray-100"}`}>{person.name}</h2>
                            {person.badge != null && <p className="text-xs text-gray-500">Badge n° {person.badge}</p>}
                        </div>
                        <button type="button" onClick={onClose} aria-label="Fermer"
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <X className="w-5 h-5"/>
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {presets.map((p) => {
                            const selected = p.range.from === range.from && p.range.to === range.to;
                            return (
                                <button key={p.key} type="button" onClick={() => setRange(p.range)}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${selected ? "bg-primary text-white border-primary" : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary"}`}>
                                    {p.label}
                                </button>
                            );
                        })}
                        <DateInput className={inputClass} value={range.from}
                               onChange={(e) => e.target.value && setRange((r) => ({from: e.target.value, to: e.target.value > r.to ? e.target.value : r.to}))}/>
                        <DateInput className={inputClass} value={range.to} min={range.from}
                               onChange={(e) => e.target.value && setRange((r) => ({...r, to: e.target.value}))}/>
                    </div>
                    {/* Counters, same look as the summary strip of the lists */}
                    <div className="grid grid-cols-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden divide-x divide-gray-200 dark:divide-gray-700">
                        {stats.map((s, index) => (
                            <div key={s.label} className={`px-3 py-2 ${index >= 4 ? "border-t border-gray-200 dark:border-gray-700" : ""}`}>
                                <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate" title={s.label}>{s.label}</div>
                                <div className={`text-base font-bold ${s.alert ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-gray-100"}`}>{data ? s.value : "…"}</div>
                            </div>
                        ))}
                    </div>
                    {!person.unknown && (
                        <div className="flex gap-1 border-b border-gray-100 dark:border-gray-700 -mb-4">
                            {tabs.map((t) => (
                                <button key={t.key} type="button" onClick={() => setView(t.key)}
                                        className={`px-4 py-2 text-sm font-medium border-b-2 ${view === t.key
                                            ? t.key === "absences" ? "border-red-500 text-red-600" : "border-primary text-primary"
                                            : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:px-6 space-y-3">
                    {!person.unknown && calendar.isError && <CalendarWarning/>}
                    {isError ? (
                        <p className="text-sm text-red-600">{apiErrorMessage(null, "Impossible de charger les pointages")}</p>
                    ) : isLoading && !data ? (
                        <Spinner/>
                    ) : view === "calendar" ? (
                        <>
                            <EmployeeCalendar cells={row.cells} today={today} selected={selectedDate} onSelect={(cell) => setSelectedDate(cell.date)}/>
                            {selectedCell
                                ? <DayDetail cell={selectedCell} person={person} editor={editor}/>
                                : <p className="text-xs text-gray-500 dark:text-gray-400 text-center">Cliquez sur un jour pour voir son détail.</p>}
                        </>
                    ) : view === "absences" ? (
                        absences.length === 0 ? (
                            <p className="text-sm text-gray-500 p-6 text-center">Aucune absence sur cette période.</p>
                        ) : (
                            <ul className={`${cardClass} divide-y divide-gray-100 dark:divide-gray-700`}>
                                {absences.map(({date}) => (
                                    <li key={date} className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100">
                                        <CalendarX className="w-4 h-4 text-red-500 flex-shrink-0"/>
                                        <span className="flex-1 capitalize">{formatLongDate(date)}</span>
                                        {editor.canManage && (
                                            <Button variant="plain" size="sm" onClick={() => editor.openLeave(person.key, date)}>Justifier</Button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )
                    ) : sortedDays.length === 0 ? (
                        <p className="text-sm text-gray-500 p-6 text-center">Aucun pointage sur cette période.</p>
                    ) : sortedDays.map((day) => (
                        <div key={day.date} className={`${cardClass} p-4`}>
                            <div className="flex items-center gap-3 mb-2">
                                <h3 className="flex-1 text-sm font-semibold text-gray-800 dark:text-gray-100 capitalize">{formatLongDate(day.date)}</h3>
                                {day.open && <StatusPill status={day.date < today ? "missing_out" : "open"}/>}
                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatDuration(day.worked_minutes)}</span>
                            </div>
                            <DayPunches day={day}/>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── Présence ────────────────────────────────────────────────────────────────

const nowInMinutes = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
};

// declareKey: bumped by the page header's « Congé / repos »
const PresenceTab = ({period, summary, isLoading, declareKey = 0}) => {
    const [detail, setDetail] = useState(null); // person + {view?, date?}
    const [withAbsences, setWithAbsences] = useState(false);
    const calendar = useRestCalendar(period.from);
    const editor = useRestEditor(calendar.resources);
    const today = toDateKey(new Date());
    const {openLeave} = editor;

    useEffect(() => {
        if (declareKey > 0) openLeave(period.employeeId || null, today);
        // Only the button press opens the form, not a change of period
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declareKey]);

    const rows = useMemo(() => (summary
        ? buildRows({
            summary,
            from: period.from,
            to: period.to,
            restDays: calendar.restDays,
            hours: calendar.hours,
            today,
            calendarReady: calendar.ready,
        }).filter((row) => !period.employeeId || row.key === period.employeeId)
        : []), [summary, period, calendar.restDays, calendar.hours, calendar.ready, today]);

    if (isLoading || !summary) return <Spinner/>;

    const singleDay = period.from === period.to;
    const visibleDays = summary.days.filter((d) => !period.employeeId || d.employee_key === period.employeeId);
    const nameById = Object.fromEntries(rows.map((r) => [r.key, r.name]));

    const sum = (field) => rows.reduce((total, row) => total + row[field], 0);
    const absencesValue = calendar.ready ? sum("absences") : "–";
    const kpis = singleDay ? [
        {label: "Présents", value: sum("worked")},
        {label: "En cours", value: sum("open")},
        {label: period.from === today ? "Pas encore pointé" : "Absents", value: period.from === today ? sum("pending") : absencesValue, alert: period.from !== today && sum("absences") > 0},
        {label: "Repos", value: sum("rests")},
        {label: "Congés", value: sum("leaves")},
        {label: "Sorties manquantes", value: sum("missingOut"), alert: sum("missingOut") > 0},
    ] : [
        {label: "Jours travaillés", value: sum("worked")},
        {label: "Heures", value: formatDuration(sum("minutes"))},
        {label: "Absences", value: absencesValue, alert: sum("absences") > 0},
        {label: "Repos", value: sum("rests")},
        {label: "Congés", value: sum("leaves")},
        {label: "Sorties manquantes", value: sum("missingOut"), alert: sum("missingOut") > 0},
    ];

    const gridRows = rows
        .filter((row) => !withAbsences || row.absences > 0)
        .sort((a, b) => (withAbsences ? b.absences - a.absences : 0) || a.name.localeCompare(b.name, "fr"));

    const openPerson = (person, extra = {}) => setDetail({...person, ...extra});

    // A click goes to what can be done about that day: justify an absence, edit
    // the repos, otherwise read the day. Read-only users always get the detail.
    const onCell = (row, cell) => {
        if (!row.unknown && editor.canManage) {
            if (cell.status === "absent" || cell.status === "pending") {
                editor.openLeave(row.key, cell.date);
                return;
            }
            if ((cell.status === "rest" || cell.status === "leave") && cell.rests[0]) {
                editor.openEdit(cell.rests[0]);
                return;
            }
        }
        openPerson(row, {view: "calendar", date: cell.date});
    };

    const nameOf = (day) => nameById[day.employee_key] || (day.employee_id ? "Employé supprimé" : `Badge n° ${day.badge_number} (non attribué)`);

    const exportCsv = () => {
        const lines = [["Date", "Employé", "Badge", "Entrée", "Sortie", "Durée (min)", "Pointages"].join(";")];
        visibleDays.forEach((day) => {
            lines.push([
                day.date, nameOf(day), day.badge_number ?? "", day.first_in ?? "", day.last_out ?? "",
                day.worked_minutes, day.punches.filter((p) => !p.duplicate).map((p) => p.time).join(" "),
            ].join(";"));
        });
        const blob = new Blob(["﻿" + lines.join("\n")], {type: "text/csv;charset=utf-8"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `pointage_${period.from}_${period.to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportAction = (
        <Button variant="tinted" size="sm" icon={Download} disabled={visibleDays.length === 0} onClick={exportCsv}>
            Export CSV
        </Button>
    );

    return (
        <div className="max-w-full">
            {calendar.isError && <CalendarWarning/>}
            <KpiStrip items={kpis}/>

            {singleDay ? (
                <Panel title={`Journée du ${formatDayLabel(period.from)}`} aside={exportAction}>
                    {rows.length === 0 ? (
                        <ListEmpty icon={Users} title="Aucun employé" text="Aucun employé avec un n° de badge."/>
                    ) : (
                        <DayTimeline
                            rows={rows}
                            window={timelineWindow(rows, period.from, calendar.hours, summary.day_start)}
                            dayStart={summary.day_start}
                            nowMinutes={nowInMinutes()}
                            onName={(row) => openPerson(row)}
                            onCell={onCell}
                        />
                    )}
                </Panel>
            ) : (
                <>
                    <Panel title="Présence par employé" aside={exportAction}>
                        <div className="flex flex-wrap items-center justify-between border-b border-gray-200 dark:border-gray-700">
                            <Legend/>
                            <label className="inline-flex items-center gap-2 px-4 sm:px-6 py-3 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                                <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary" checked={withAbsences}
                                       onChange={(e) => setWithAbsences(e.target.checked)}/>
                                Seulement avec des absences
                            </label>
                        </div>
                        {gridRows.length === 0 ? (
                            <ListEmpty icon={Users} title={withAbsences ? "Aucune absence" : "Aucun employé"}
                                       text={withAbsences ? "Aucune absence sur cette période." : "Aucun employé avec un n° de badge."}/>
                        ) : (
                            <PresenceGrid rows={gridRows} today={today} onName={(row) => openPerson(row, withAbsences ? {view: "absences"} : {})} onCell={onCell}/>
                        )}
                    </Panel>

                    <Panel title="Détail par jour">
                        {visibleDays.length === 0 ? (
                            <ListEmpty icon={CalendarDays} title="Aucun pointage" text="Aucun pointage sur cette période."/>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                    <tr>
                                        <th className={thClass}>Jour</th>
                                        <th className={thClass}>Employé</th>
                                        <th className={thClass}>Entrée</th>
                                        <th className={thClass}>Sortie</th>
                                        <th className={`${thClass} text-right`}>Durée</th>
                                        <th className={thClass}>Pointages</th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {visibleDays.map((day) => (
                                        <tr key={`${day.employee_key}-${day.date}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className={`${tdClass} whitespace-nowrap text-gray-500 dark:text-gray-400`}>{formatDayLabel(day.date)}</td>
                                            <td className={`${tdClass} whitespace-nowrap`}>
                                                <button type="button"
                                                        onClick={() => openPerson({key: day.employee_key, name: nameOf(day), badge: day.badge_number, unknown: !day.employee_id, active: rows.find((r) => r.key === day.employee_key)?.active})}
                                                        className={`hover:underline text-left font-semibold ${day.employee_id ? "text-gray-900 dark:text-gray-100" : "text-amber-600"}`}>
                                                    {nameOf(day)}
                                                </button>
                                            </td>
                                            <td className={`${tdClass} text-gray-700 dark:text-gray-300`}>{day.first_in || "–"}</td>
                                            <td className={`${tdClass} whitespace-nowrap text-gray-700 dark:text-gray-300`}>
                                                {day.last_out || (day.open
                                                    ? <span className={day.date < today ? "text-red-600 dark:text-red-400 font-semibold" : "text-green-600 dark:text-green-400"}>
                                                        {day.date < today ? "Manquante" : "En cours"}
                                                      </span>
                                                    : "–")}
                                            </td>
                                            <td className={`${tdClass} text-right font-semibold text-gray-900 dark:text-gray-100`}>{formatDuration(day.worked_minutes)}</td>
                                            <td className={tdClass}>
                                                <div className="flex flex-wrap gap-1">
                                                    {day.punches.map((p) => <PunchChip key={p.id} punch={p}/>)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Panel>
                </>
            )}

            <p className="px-4 sm:px-6 py-4 text-xs text-gray-500 dark:text-gray-400">
                Les repos et congés viennent du calendrier, et les jours fermés de ses horaires d'ouverture : ils ne comptent jamais comme des absences.
                Les pointages d'une journée sont alternés entrée / sortie. Deux pointages à moins de 2 minutes d'intervalle comptent pour un seul.
            </p>
            {detail && (
                <EmployeeAttendanceModal
                    key={detail.key}
                    person={detail}
                    listPeriod={period}
                    initialView={detail.view}
                    initialDate={detail.date}
                    editor={editor}
                    onClose={() => setDetail(null)}
                />
            )}
            {editor.element}
        </div>
    );
};

export default PresenceTab;
